/**
 * P6-18 · Pure popup logic — targeting, frequency, and trigger evaluation.
 * Client-safe + isomorphic: the server uses `matchesTargeting`/`underFrequencyCap`
 * to pick eligible popups; the client uses `shouldTrigger` in the trigger engine.
 * All pure, so unit-tested in isolation.
 */
import type { PopupFrequency, PopupTargeting, PopupTrigger } from "./popup-config";

export interface TargetingContext {
  /** The current storefront path (e.g. "/products/scarf"). */
  path: string;
  /** True when the visitor has been here before (has a popup session / account). */
  isReturning: boolean;
  isMobile: boolean;
}

/** Whether a popup's targeting rules admit the current visitor/page. */
export function matchesTargeting(t: PopupTargeting, ctx: TargetingContext): boolean {
  if (t.paths.length > 0 && !t.paths.some((p) => ctx.path.startsWith(p))) return false;
  if (t.audience === "new" && ctx.isReturning) return false;
  if (t.audience === "returning" && !ctx.isReturning) return false;
  if (t.device === "desktop" && ctx.isMobile) return false;
  if (t.device === "mobile" && !ctx.isMobile) return false;
  return true;
}

/**
 * Whether the popup may show again given prior impressions: under the total cap
 * AND past the cooldown since the last show.
 */
export function underFrequencyCap(
  shownCount: number,
  lastShownAt: Date | null,
  freq: PopupFrequency,
  now: Date,
): boolean {
  if (shownCount >= freq.maxImpressions) return false;
  if (lastShownAt && freq.cooldownDays > 0) {
    const nextAllowed = lastShownAt.getTime() + freq.cooldownDays * 86_400_000;
    if (nextAllowed > now.getTime()) return false;
  }
  return true;
}

export interface TriggerState {
  /** Milliseconds since the page loaded. */
  elapsedMs: number;
  /** Furthest scroll depth reached, 0–100. */
  scrollPct: number;
  /** An exit-intent signal has fired (mouseleave / mobile scroll-up + idle). */
  exitSignal: boolean;
}

/** Whether any of the popup's enabled triggers is satisfied by the state. */
export function shouldTrigger(trigger: PopupTrigger, state: TriggerState): boolean {
  if (trigger.exitIntent && state.exitSignal) return true;
  if (trigger.scrollPct > 0 && state.scrollPct >= trigger.scrollPct) return true;
  if (trigger.delayMs > 0 && state.elapsedMs >= trigger.delayMs) return true;
  return false;
}
